import { useQuery } from "@tanstack/react-query";
import {
  AdminStatsService,
  type StatsPeriod,
  type AdminStatsResponse,
  type AdminStatsTimeSeriesResponse,
  type StatsTimeSeriesInterval,
  type GetAdminLatestBidsResponse,
  type AdminStatsDateRange,
} from "@/services/admin/adminStatsService";

const ADMIN_STATS_REFETCH_MS = 5 * 60 * 1000; // 5 minutes

export function useAdminStats(
  period: StatsPeriod = "1w",
  enabled = true,
  dateRange?: AdminStatsDateRange
) {
  const statsQuery = useQuery<AdminStatsResponse>({
    queryKey: ["adminStats", period, dateRange?.from, dateRange?.to],
    queryFn: () => AdminStatsService.getStats(period, dateRange),
    enabled,
    refetchInterval: ADMIN_STATS_REFETCH_MS,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  return {
    stats: statsQuery.data,
    isLoading: statsQuery.isLoading,
    isFetching: statsQuery.isFetching,
    error: statsQuery.error,
    refetch: statsQuery.refetch,
    period,
  };
}

export function useAdminStatsTimeSeries(
  period: StatsPeriod = "1w",
  interval: StatsTimeSeriesInterval = "day",
  enabled = true,
  dateRange?: AdminStatsDateRange
) {
  const timeSeriesQuery = useQuery<AdminStatsTimeSeriesResponse>({
    queryKey: ["adminStatsTimeSeries", period, interval, dateRange?.from, dateRange?.to],
    queryFn: () => AdminStatsService.getTimeSeries(period, interval, dateRange),
    enabled,
    staleTime: 2 * 60 * 1000,
  });

  return {
    timeSeries: timeSeriesQuery.data,
    isLoading: timeSeriesQuery.isLoading,
    error: timeSeriesQuery.error,
    refetch: timeSeriesQuery.refetch,
  };
}

const LATEST_BIDS_PAGE_SIZE = 10;

export function useAdminLatestBids(page: number = 1, enabled = true) {
  const query = useQuery<GetAdminLatestBidsResponse>({
    queryKey: ["adminLatestBids", page],
    queryFn: () => AdminStatsService.getLatestBids(page, LATEST_BIDS_PAGE_SIZE),
    enabled,
    staleTime: 1 * 60 * 1000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    page,
    pageSize: LATEST_BIDS_PAGE_SIZE,
  };
}
